import type { OfferSubmissionDocument, OfferSubmissionMode, OfferSubmissionStatus } from '@apparatix/types/Documents';

/**
 * Wording for the Submit Offer / Submit Contract flow.
 *
 * Enterprise tenants send the signed document to a manager for approval, so
 * it is an "offer" until approved. Essentials tenants have no approval step,
 * so the same document is a "contract" from the moment it is submitted.
 */
export type TagSeverity = 'secondary' | 'info' | 'warn' | 'success';

export type StatusCopy = {
  label: string;
  severity: TagSeverity;
  copy: string;
};

export type FormCopy = {
  title: string;
  heading: string;
  body: string;
  warning: string | null;
  confirmLabel: string;
  successTitle: string;
  successMessage: string;
  errorTitle: string;
  errorMessage: string;
};

export const sectionHeading = (isEssentials: boolean) => (isEssentials ? 'Contract' : 'Offer');

export const documentSubtitle = (document: OfferSubmissionDocument) => (document.entityName
  ? `Document ${document.displayId} · ${document.entityName}`
  : `Document ${document.displayId}`);

export const statusCopy = (status: OfferSubmissionStatus, isEssentials: boolean): StatusCopy => {
  const document = isEssentials ? 'contract' : 'offer';

  switch (status) {
    case 'ready':
      return {
        label: 'Ready to submit',
        severity: 'info',
        copy: isEssentials
          ? 'Upload the signed PDF to submit this contract.'
          : 'Upload the signed PDF to submit this offer for approval.',
      };
    case 'awaiting-approval':
      return {
        label: 'Awaiting approval',
        severity: 'warn',
        copy: 'Offer is awaiting approval. Submitting again replaces the received PDF.',
      };
    case 'contracted':
      return {
        label: isEssentials ? 'Submitted' : 'Contracted',
        severity: 'success',
        copy: isEssentials
          ? 'The signed contract has been submitted.'
          : 'This offer has been approved and is now a contract.',
      };
    case 'not-offer':
    default:
      return {
        label: 'Not ready to submit',
        severity: 'secondary',
        copy: `Save this document as an offer before the signed ${document} can be submitted.`,
      };
  }
};

/** Shown in place of the server status right after a successful submit, until the queued job catches up. */
export const submittedStatusCopy = (mode: OfferSubmissionMode, isEssentials: boolean): StatusCopy => (mode === 'contract'
  ? {
    label: isEssentials ? 'Submitted' : 'Contract submitted',
    severity: 'success',
    copy: 'The signed PDF was received. The contract is being created.',
  }
  : {
    label: 'Awaiting approval',
    severity: 'warn',
    copy: 'Offer submitted for approval. Submitting again replaces the received PDF.',
  });

export const formCopy = (mode: OfferSubmissionMode, isEssentials: boolean): FormCopy => (mode === 'contract'
  ? {
    title: 'Submit Contract',
    heading: 'Submit the signed contract',
    body: 'Upload the signed PDF to submit this contract.',
    warning: isEssentials
      ? 'This creates the contract immediately. It cannot be undone here.'
      : 'This creates the contract immediately, skipping approval. It cannot be undone here.',
    confirmLabel: 'Submit Contract',
    successTitle: 'Contract submitted',
    successMessage: 'The contract is being created.',
    errorTitle: 'Contract submission failed',
    errorMessage: 'The contract was not submitted.',
  }
  : {
    title: 'Submit Offer',
    heading: 'Submit the signed offer',
    body: 'Upload the signed PDF to submit this offer for approval. Users assigned to review offers are notified automatically.',
    warning: null,
    confirmLabel: 'Submit for Approval',
    successTitle: 'Offer submitted',
    successMessage: 'Offer submitted for approval.',
    errorTitle: 'Offer submission failed',
    errorMessage: 'The offer was not submitted.',
  });
